import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import { ComponentsModule } from '@freelancer/components';
import { Datastore } from '@freelancer/datastore';
import {
  DatastoreAiSmartCanvasMakeRealModule,
  type AiSmartCanvasMakeRealCollection,
  type User,
  type UsersCollection,
} from '@freelancer/datastore/collections';
import { ThemeService } from '@freelancer/theme';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, firstValueFrom, map } from 'rxjs';
import type {
  FuncContainer,
  MakeRealFunc,
  TldrawWCUserProps,
} from 'tldraw-web-component';
import { v4 as uuid } from 'uuid';
import { CANVAS_CONFIG } from './canvas.config';
import { CanvasConfig } from './canvas.interface';
import { CanvasService } from './canvas.service';

@UntilDestroy({ className: 'CanvasComponent' })
@Component({
  standalone: true,
  selector: 'fl-canvas',
  styleUrls: ['./canvas.component.scss'],
  imports: [
    CommonModule,
    ComponentsModule,
    TrackingModule,
    UiModule,
    DatastoreAiSmartCanvasMakeRealModule,
  ],
  template: `<span [id]="tldrawContainerId"></span>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * This component is used to render the canvas for the Smart Canvas Playground.
 * This component will first fetch an access token from the backend, and then use that token to authenticate with the canvas server.
 *
 * ### The reason for attaching functions to the window.
 *
 * Note: This component was originally a React component, but due to issues with how newer versions of React and Angular interact,
 * with some very annoying re-rendering issues and various other bugs, we decided to convert it to create a web component and use it in Angular.
 *
 * With web components, it is not as easy to pass data to the component as it is with Angular components, so we had to use global functions.
 * In pure html, you normally pass the name of the function to the attribute of the web component, and the web component will call that function.
 *
 * Angular inherently does not make it easy to pass functions into components,
 * as we don't even know the final name of the function until after it is compiled, and even then, it will be in some random chunk.
 *
 * So to keep things simple, we create global functions that the web component can call, and then we pass the name of the function to the web component.
 *
 * #### NOTES:
 * - One must remember to clean up the global functions when the component is destroyed.
 * - For documentations on the functions, see misc/tldraw-web-component/
 */
export class CanvasComponent implements OnInit, OnDestroy {
  @Input() contextType: string;
  @Input() contextId: number;
  @Input() debug = false;

  user: User;
  darkMode = false;

  tldrawContainerId = `tldraw-component-${uuid()}`;
  private globalFuncContainerName = `__SECRET_DOM_DO_NOT_USE_FUNC_CONTAINER_${uuid()}`;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(CANVAS_CONFIG) private canvasConfig: CanvasConfig,
    @Inject(DOCUMENT) private doc: Document,
    private auth: Auth,
    private canvasService: CanvasService,
    private datastore: Datastore,
    private themeService: ThemeService,
  ) {}

  async ngOnInit(): Promise<void> {
    // The entire canvas component is only rendered on the browser
    if (isPlatformBrowser(this.platformId)) {
      // Import is dynamic because pre-rendering throws type errors.
      await import('tldraw-web-component');

      const { canvasHost } = this.canvasConfig;
      if (!canvasHost) {
        throw new Error('Canvas host is not defined.');
      }

      const userId$ = this.auth.getUserId().pipe(filter(userId => !!userId));
      const user$ = this.datastore
        .document<UsersCollection>('users', userId$)
        .valueChanges();
      this.user = await firstValueFrom(user$.pipe(untilDestroyed(this)));

      const accessToken = await this.canvasService.fetchAccessToken({
        contextId: this.contextId,
        contextType: this.contextType,
      });

      if (!accessToken) {
        throw new Error('Failed to access the canvas');
      }

      const container = this.doc.querySelector(`#${this.tldrawContainerId}`);
      if (!container) {
        console.error(`#${this.tldrawContainerId} container not found`);
        return;
      }
      const tldrawElement = this.doc.createElement('tldraw-sync-web-component');

      if (this.debug) {
        tldrawElement.setAttribute('debug', '1');
      }

      tldrawElement.setAttribute('server-uri', canvasHost);
      tldrawElement.setAttribute(
        'room-id',
        `${this.contextType}:${this.contextId}`,
      );
      tldrawElement.setAttribute(
        'query-params',
        JSON.stringify({
          accessToken,
        }),
      );
      try {
        this.darkMode = await firstValueFrom(
          this.themeService.getTheme().pipe(
            map(theme => theme === 'dark'),
            untilDestroyed(this),
          ),
        );
      } catch (e) {
        console.log(e);
      }
      const getFuncContainer = this.getFuncContainer.bind(this);
      (window as any)[this.globalFuncContainerName] = getFuncContainer;

      tldrawElement.setAttribute(
        'get-func-container',
        this.globalFuncContainerName,
      );
      container.appendChild(tldrawElement);
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      delete (window as any)[this.globalFuncContainerName];
    }
  }

  public getUser(): TldrawWCUserProps {
    return {
      id: this.user.id.toString(),
      name: this.user.username,
      colorScheme: this.darkMode ? 'dark' : 'light',
    };
  }

  private getFuncContainer(): FuncContainer {
    const makeRealFunc: MakeRealFunc = async (prompt, image, messages) => {
      const formattedMessages = messages.map(message => {
        return {
          type: message.type,
          content: message.text || message.image,
        };
      });
      const response =
        await this.datastore.createDocument<AiSmartCanvasMakeRealCollection>(
          'aiSmartCanvasMakeReal',
          {
            developerPrompt: prompt,
            base64Image: image,
            messages: formattedMessages,
            returnValue: '',
          },
        );

      if (response.status !== 'success') {
        throw new Error('Failed to make it real');
      }
      const newMessage = await firstValueFrom(
        this.datastore
          .document<AiSmartCanvasMakeRealCollection>(
            'aiSmartCanvasMakeReal',
            response.id,
          )
          .valueChanges()
          .pipe(untilDestroyed(this)),
      );
      return newMessage.returnValue;
    };

    const multiplayerAssetsFunc: FuncContainer['multiplayerAssetsFunc'] =
      () => {
        return {
          upload: async (asset, file) => {
            const data = await this.canvasService.uploadFile({
              file,
              contextId: this.contextId,
              contextType: this.contextType,
            });

            asset.meta = {
              uuid: data.fileUuid,
            };
            return {
              src: data.url,
            };
          },
          resolve: async asset => {
            if (typeof asset.meta.uuid !== 'string') {
              throw new Error('Asset meta is not a string');
            }

            const response = await this.canvasService.fetchFile({
              fileUuid: asset.meta.uuid,
              contextId: this.contextId,
              contextType: this.contextType,
            });

            if (response.status === 'success') {
              return response.result.url;
            }
            throw new Error('Failed to fetch the file');
          },
        };
      };

    const getUserFunc = this.getUser.bind(this);
    return {
      multiplayerAssetsFunc,
      getUserFunc,
      makeRealFunc,
    };
  }
}
